import type { CotCircleType } from '../types/community'

// Display capabilities describe implemented surfaces, not planned features or proof of identity.

export type CircleTypeKey = 'gaming' | 'family' | 'friends'

export interface CapabilityRow {
  on: boolean
  title: string
  body: string
}

export interface CircleTypeMeta {
  /** Lowercase create key sent to POST /circles. */
  key: CircleTypeKey
  /** Server enum name. */
  typeName: CotCircleType
  /** Full label ("Gaming circle"). */
  label: string
  /** Type-dot hex colour. */
  dot: string
  tagline: string
  caps: CapabilityRow[]
}

export const CIRCLE_TYPES: CircleTypeMeta[] = [
  {
    key: 'gaming',
    typeName: 'Gaming',
    label: 'Gaming circle',
    dot: '#7fa66b',
    tagline: 'Play together in one game, with real gamertags shared only inside the circle.',
    caps: [
      { on: true, title: 'Private player IDs', body: 'Gamertags, friend codes and server addresses are visible to circle members only — never public, never indexed.' },
      { on: true, title: 'Live rooms + join codes', body: 'Open a room and share game join details with its members.' },
      { on: false, title: 'Session scheduling', body: 'Scheduled sessions are not available yet.' },
      { on: false, title: 'Knowzai records', body: 'Not available in gaming circles — no personal records are shared.' },
      { on: true, title: 'Confidential reports', body: 'Reports are available to the reporting family and authorised moderators. Hosts and reported families do not receive the report.' },
    ],
  },
  {
    key: 'family',
    typeName: 'Family',
    label: 'Family trust circle',
    dot: '#6f8fbf',
    tagline: 'Families you invite and trust. Payment verification does not establish identity or relationships.',
    caps: [
      { on: true, title: 'Family roster', body: 'See family and profile information shared inside this circle.' },
      { on: false, title: 'Knowzai knowledge access', body: 'Sharing personal records is not available in this app.' },
      { on: false, title: 'Emergency contact chain', body: 'Emergency contacts and escalation are not available in this app.' },
      { on: true, title: 'Play together', body: 'Rooms and codes work here too, with the same supervision rules.' },
      { on: true, title: 'Circle activity', body: 'See the activity available to your profile. Reports remain confidential.' },
    ],
  },
  {
    key: 'friends',
    typeName: 'Friends',
    label: 'Friend circle',
    dot: '#b9704f',
    tagline: 'Kids who know each other in real life. Deliberately the least sharing.',
    caps: [
      { on: true, title: 'Family roster', body: 'See family and profile information shared inside this circle.' },
      { on: true, title: 'Live rooms + join codes', body: 'Join a room a friend is already in.' },
      { on: false, title: 'Private player IDs', body: 'No separate player-ID sharing setting is available. Game details can be shared inside permitted rooms.' },
      { on: false, title: 'Knowzai records', body: 'Never shared in a friend circle.' },
      { on: true, title: 'Confidential reports', body: 'Reports are available to the reporting family and authorised moderators. Parent notes are a separate action.' },
    ],
  },
]

/** Look up the capability matrix for a server circle-type name. */
export function capabilitiesFor(type: CotCircleType): CapabilityRow[] {
  return CIRCLE_TYPES.find((t) => t.typeName === type)?.caps ?? []
}

/** Look up the tagline for a server circle-type name. */
export function taglineFor(type: CotCircleType): string {
  return CIRCLE_TYPES.find((t) => t.typeName === type)?.tagline ?? ''
}
